import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { OutlineButton } from "../Buttons";
import { showSnackbar } from "../../redux/snackbarSlice";
import { buildPermissionContext } from "../../util/permissions";

/**
 * UploadViaCsvBtn - Button for uploading/updating data via CSV file
 *
 * @param {Object} props
 * @param {React.RefObject} props.fileInputRef - Reference to hidden file input
 * @param {function} props.handleFileChange - File change handler
 * @param {boolean} props.disabled - Whether the button is disabled
 *
 * @example
 * <UploadViaCsvBtn fileInputRef={fileInputRef} handleFileChange={handleFileChange} />
 */
const UploadViaCsvBtn = ({ fileInputRef, handleFileChange, disabled = false }) => {
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true && !disabled;

  const handleFileValidation = (e) => {
    if (!canEdit || disabled) {
      e.target.value = "";
      return;
    }
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    // Check if file extension is .csv
    const fileName = file.name.toLowerCase();
    const isValidCsv = fileName.endsWith(".csv");

    if (!isValidCsv) {
      dispatch(
        showSnackbar({
          type: "error",
          message:
            "Please select a valid CSV file. Only .csv files are allowed.",
        })
      );
      // Reset the input value to allow selecting the same file again after error
      e.target.value = "";
      return;
    }

    // If valid CSV, proceed with the original handler
    handleFileChange(e, "upload");
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".csv"
        onChange={handleFileValidation}
        disabled={disabled}
      />
      <OutlineButton
        variant="outline-blue"
        size="md"
        onClick={() => canEdit && !disabled && fileInputRef.current?.click()}
        disabled={!canEdit || disabled}
        style={{
          marginBottom: "0px",
          marginLeft: "0px",
          opacity: canEdit && !disabled ? 1 : 0.5,
          cursor: canEdit && !disabled ? "pointer" : "not-allowed",
        }}
      >
        Add or Update via CSV
      </OutlineButton>
    </>
  );
};

export default UploadViaCsvBtn;
